<template>
  <section class="section">
    <div class="container">
      <Header v-bind="$route.meta" />
      <b-loading v-model="isLoading" :is-full-page="false" />

      <div class="level">
        <div class="level-left">
          <h1 class="title is-4">
            {{ $t('pages.crate_validation_rules.title') }}
          </h1>
        </div>
        <div class="level-right">
          <b-button type="is-primary" icon-left="plus" @click="openAdd">
            {{ $t('pages.crate_validation_rules.addRule') }}
          </b-button>
        </div>
      </div>

      <b-table :data="rules" :paginated="rules.length > 20" :per-page="20" hoverable>
        <b-table-column v-slot="props" field="code" :label="$t('pages.crate_validation_rules.code')" sortable>
          {{ props.row.code }}
        </b-table-column>
        <b-table-column v-slot="props" field="description" :label="$t('pages.crate_validation_rules.description')">
          {{ props.row.description }}
        </b-table-column>
        <b-table-column v-slot="props" field="ruleType" :label="$t('pages.crate_validation_rules.ruleType')" sortable>
          {{ props.row.ruleType }}
        </b-table-column>
        <b-table-column
          v-slot="props"
          field="enabled"
          :label="$t('pages.crate_validation_rules.enabled')"
          centered
          sortable
        >
          <b-tag :type="props.row.enabled ? 'is-success' : 'is-light'">
            {{ props.row.enabled ? $t('global.yes') : $t('global.no') }}
          </b-tag>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.crate_validation_rules.params')">
          <code class="params-cell">{{ stringify(props.row.params) }}</code>
        </b-table-column>
        <b-table-column v-slot="props" :label="$t('pages.crate_validation_rules.actions')" centered width="120">
          <b-button size="is-small" icon-left="pen" @click="openEdit(props.row)">
            {{ $t('pages.crate_validation_rules.edit') }}
          </b-button>
        </b-table-column>

        <template #empty>
          <div class="has-text-centered has-text-grey p-4">
            {{ $t('pages.crate_validation_rules.empty') }}
          </div>
        </template>
      </b-table>
    </div>

    <b-modal v-model="modalOpen" :width="600" scroll="keep">
      <div class="card">
        <header class="card-header">
          <p class="card-header-title">
            {{ isEdit ? $t('pages.crate_validation_rules.editRule') : $t('pages.crate_validation_rules.addRule') }}
          </p>
        </header>
        <div class="card-content">
          <b-field :label="$t('pages.crate_validation_rules.code')">
            <b-input v-model="form.code" :disabled="isEdit" />
          </b-field>

          <b-field :label="$t('pages.crate_validation_rules.description')">
            <b-input v-model="form.description" :disabled="isEdit" type="textarea" rows="2" />
          </b-field>

          <b-field :label="$t('pages.crate_validation_rules.enabled')">
            <b-switch v-model="form.enabled" />
          </b-field>

          <b-field :label="$t('pages.crate_validation_rules.ruleType')">
            <b-autocomplete
              v-model="form.ruleType"
              :data="filteredRuleTypes"
              :placeholder="$t('pages.crate_validation_rules.ruleTypePlaceholder')"
              open-on-focus
              clearable
            />
          </b-field>

          <b-field
            :label="$t('pages.crate_validation_rules.params')"
            :message="$t('pages.crate_validation_rules.paramsHint')"
          >
            <b-input v-model="form.paramsText" type="textarea" rows="6" class="is-family-monospace" />
          </b-field>
        </div>
        <footer class="card-footer p-3">
          <b-button class="mr-2" @click="modalOpen = false">
            {{ $t('global.cancel') }}
          </b-button>
          <b-button type="is-primary" :loading="isSaving" @click="submit">
            {{ $t('global.save') }}
          </b-button>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
import CrateValidationRule from '@/services/v2/crateValidationRule'

export default {
  name: 'CrateValidationRules',
  data () {
    return {
      isLoading: false,
      isSaving: false,
      rules: [],
      modalOpen: false,
      isEdit: false,
      form: {
        id: null,
        code: '',
        description: '',
        enabled: true,
        ruleType: '',
        paramsText: '{}'
      }
    }
  },
  computed: {
    ruleTypes () {
      return [...new Set(this.rules.map(r => r.ruleType).filter(Boolean))]
    },
    filteredRuleTypes () {
      const q = (this.form.ruleType || '').toLowerCase()
      return this.ruleTypes.filter(t => t.toLowerCase().includes(q))
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    async loadData () {
      this.isLoading = true
      try {
        this.rules = await CrateValidationRule.getAll()
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 4000 })
      } finally {
        this.isLoading = false
      }
    },
    stringify (value) {
      if (value === null || value === undefined) return ''
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
    openAdd () {
      this.isEdit = false
      this.form = { id: null, code: '', description: '', enabled: true, ruleType: '', paramsText: '{}' }
      this.modalOpen = true
    },
    openEdit (row) {
      this.isEdit = true
      this.form = {
        id: row.id,
        code: row.code,
        description: row.description,
        enabled: !!row.enabled,
        ruleType: row.ruleType || '',
        paramsText: row.params === null || row.params === undefined ? '' : JSON.stringify(row.params, null, 2)
      }
      this.modalOpen = true
    },
    parseParams () {
      const text = (this.form.paramsText || '').trim()
      if (text === '') return null
      return JSON.parse(text)
    },
    async submit () {
      let params
      try {
        params = this.parseParams()
      } catch (e) {
        this.$buefy.toast.open({ type: 'is-danger', message: this.$t('pages.crate_validation_rules.invalidJson'), duration: 4000 })
        return
      }

      if (!this.isEdit && (!this.form.code.trim() || !this.form.description.trim())) {
        this.$buefy.toast.open({ type: 'is-danger', message: this.$t('pages.crate_validation_rules.requiredFields'), duration: 4000 })
        return
      }

      this.isSaving = true
      try {
        if (this.isEdit) {
          await CrateValidationRule.update(this.form.id, {
            enabled: this.form.enabled,
            ruleType: this.form.ruleType || null,
            params
          })
        } else {
          await CrateValidationRule.create({
            code: this.form.code.trim(),
            description: this.form.description.trim(),
            ruleType: this.form.ruleType || null,
            enabled: this.form.enabled,
            params
          })
        }
        this.modalOpen = false
        this.$buefy.toast.open({ type: 'is-primary', message: this.$t('pages.crate_validation_rules.saved'), duration: 3000 })
        await this.loadData()
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 4000 })
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
.params-cell {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  background: transparent;
  color: #4a4a55;
}
</style>
